import { api } from "./base-config";
import {
  Journal,
  JournalEntry,
  JournalEntryContainer,
  JournalEntryItem,
  JournalEntryItemType,
  JournalEntryType
} from "model/journal.model";
import { DateMaskEnum } from "common/enums/DateMaskEnum";
import { ConfidentialityTypeEnum } from "common/enums/ConfidentialityTypeEnum";
import { StatusTypeEnum, translateKey } from "common/enums/StatusTypeEnum";
import { ActionDate } from "model/action-date.model";
import moment from "moment";
import { ContactTypeEnum } from "common/enums/ContactTypeEnum";
import { PaginatedSearchJournalRequest } from "model/paginated-journal-request.model";

/** ---- API CALLS ---- */
export const findAllJournalEntries = (
  caregiverId: number,
  patientId: number,
  paginatedRequest: PaginatedSearchJournalRequest
): Promise<Journal> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/journalOverview`,
      paginatedRequest
    )
    .then(response => mapToJournalModel(response.data));
};

/** ---- API MAPPING ---- */
const mapToJournalModel = (response: JournalEntriesSearchResponseDTO) => {
  const journal: Journal = new Journal();
  response.journalEntriesContainers.forEach(
    (container: JournalEntriesContainer) => {
      const contact: Contact = container.contact;
      const containerId: number = contact.id;
      const journalEntries: Array<JournalEntryType> = [];

      container.journalEntries.forEach((entry: JournalEntryDTO) => {
        const subContact: SubContact = entry.subContact;
        const journalEntryItems: Array<JournalEntryItemType> = [];

        entry.patientRecordItems.forEach(
          (patientRecordItem: PatientRecordItem) => {
            const journalEntryItem: JournalEntryItem = new JournalEntryItem(
              patientRecordItem.id,
              determineType(patientRecordItem),
              determineTitle(patientRecordItem),
              determineStatusKey(patientRecordItem)
            );
            journalEntryItems.push(journalEntryItem);
          }
        );

        const journalEntry: JournalEntry = new JournalEntry(
          containerId,
          subContact.title ?? "",
          subContact.authorID,
          new ActionDate(
            moment(subContact.actionDate).toDate(),
            subContact.actionDateMask
          ),
          journalEntryItems
        );

        journal.journalEntryItems.push(...journalEntryItems);
        journalEntries.push(journalEntry);
      });

      const journalEntryContainer: JournalEntryContainer = new JournalEntryContainer(
        contact.id,
        contact.authorID,
        contact.type,
        new ActionDate(
          moment(contact.actionDate).toDate(),
          contact.actionDateMask
        )
      );

      journal.journalEntries[containerId] = journalEntries;
      journal.journalEntryContainers.push(journalEntryContainer);
    }
  );

  journal.processedIds.push(...response.processedIds);

  return journal;
};

const determineType = (patientRecordItem: PatientRecordItem): string => {
  const patientRecordItemType = patientRecordItem["@type"];
  switch (patientRecordItemType) {
    case "SOEPRule":
      return patientRecordItem.type;
    default:
      return patientRecordItemType;
  }
};

const determineTitle = (patientRecordItem: PatientRecordItem): string => {
  const type = patientRecordItem["@type"];
  switch (type) {
    case "Episode":
      return patientRecordItem.title;
    case "ParameterGroupEntry":
      const entry = patientRecordItem.entry;
      return (
        entry.codeDescription +
        ": " +
        entry.formattedValue +
        (entry.unitDescription ? " " + entry.unitDescription : "")
      );
    default:
      return patientRecordItem.freeTextContent;
  }
};

const determineStatusKey = (patientRecordItem: PatientRecordItem): string => {
  const type = patientRecordItem["@type"];
  switch (type) {
    case "MedicationEntry":
      return translateKey(patientRecordItem.status);
    default:
      return "";
  }
};

/** ---- API DTO ---- */
interface JournalEntriesSearchResponseDTO {
  readonly journalEntriesContainers: Array<JournalEntriesContainer>;
  readonly processedIds: Array<number>;
}
interface JournalEntriesContainer {
  readonly contact: Contact;
  readonly journalEntries: JournalEntryDTO[];
}

interface Contact {
  readonly id: number;
  readonly authorID: number;
  readonly type: ContactTypeEnum;
  readonly actionDate: string;
  readonly actionDateMask: DateMaskEnum;
}
interface JournalEntryDTO {
  readonly patientRecordItems: PatientRecordItem[];
  readonly subContact: SubContact;
}
interface SubContact {
  readonly id: number;
  readonly title: string;
  readonly authorID: number;
  readonly actionDate: string;
  readonly actionDateMask: DateMaskEnum;
}
interface PatientRecordItem {
  readonly "@type": string;
  readonly confidentiality: ConfidentialityTypeEnum;
  readonly authorID: number;
  readonly id: number;
  readonly ownerID: number;
  readonly status: keyof typeof StatusTypeEnum;
  readonly title: string;
  readonly freeTextContent: string;
  readonly entry: ResultEntry;
  readonly type: string;
}

interface ResultEntry {
  readonly "@type": string;
  readonly codeDescription: string;
  readonly formattedValue: string;
  readonly unitDescription: string;
}
